<template>
    <div class="token-hist-container">
        <div class="token-hist__title--big">
            {{ $t('paygo.tokenHistory.title', { i18nFallback: 'Token History' }) }}
        </div>
        <div class="token-hist__title--small">
            {{
                $t('paygo.tokenHistory.text', {
                    i18nFallback: 'Below is your detailed activity overview.',
                })
            }}
        </div>

        <vrm-foldable-table :items="tokens" key-by="idPaygoToken">
            <template v-slot:thead>
                <tr>
                    <th>{{ $t('paygo.tokenHistory.date', { i18nFallback: 'Date' }) }}</th>
                    <th>
                        {{ $t('paygo.tokenHistory.tokenType', { i18nFallback: 'Token type' }) }}
                    </th>
                    <th>
                        {{ $t('paygo.tokenHistory.tokenCode', { i18nFallback: 'Token code' }) }}
                    </th>
                    <th>{{ $t('paygo.tokenHistory.amount', { i18nFallback: 'Amount' }) }}</th>
                    <th>{{ $t('paygo.tokenHistory.paid', { i18nFallback: 'Paid' }) }}</th>
                    <th>
                        {{ $t('paygo.tokenHistory.generatedBy', { i18nFallback: 'Generated by' }) }}
                    </th>
                    <th />
                </tr>
            </template>
            <template v-slot:main="{ item }">
                <td>
                    {{ getDate(item.start) }}
                </td>
                <td>
                    {{ getTokenTypeText(item.tokenType) }}
                </td>
                <td>
                    {{ formatTokenCode(item.code) }}
                </td>
                <td class="token-line__amount">
                    <div class="token-line__amount__label">
                        {{ getCurrency(site.paygoCurrency) }}
                    </div>
                    <input
                        v-model.number="item.amount"
                        class="token-line__amount__input"
                        :class="{
                            'token-line__amount__input--disabled': item.isPaid,
                            'token-line__amount__input--active': !item.isPaid,
                        }"
                        type="text"
                        :disabled="item.isPaid"
                        @change="onAmountChange(item.amount, item.idPaygoToken)"
                        @keypress="isNumber($event)"
                        @paste="onAmountPaste($event)"
                    />
                </td>
                <td>
                    <label>
                        <input
                            v-model="item.isPaid"
                            type="checkbox"
                            :disabled="false"
                            @change="onIsPaidChange(item)"
                        />
                        <div
                            class="token-line__check"
                            :class="{
                                'token-line__check--active': item.isPaid,
                                'token-line__check--passive': !item.isPaid,
                            }"
                        ></div>
                        <div class="sg-caption-container">
                            <div class="sg-caption"></div>
                        </div>
                    </label>
                </td>
                <td>{{ item.author }}</td>
                <td class="vrm-table__actions">
                    <i
                        v-if="item.isGroup"
                        class="vrm-table__icon--link"
                        :class="
                            expanded
                                ? 'sg-vrm-icon_059_min-round-16px'
                                : 'sg-vrm-icon_058_plus-round-16px'
                        "
                        @click="toggle"
                    />
                </td>
            </template>
        </vrm-foldable-table>
    </div>
</template>
<style>
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
</style>
<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmPaygoTokenHistoryLine from '@/vue/components/VrmPaygoTokenHistoryLine.vue';
import VrmFoldableTable from '@/vue/components/molecules/VrmFoldableTable.vue';
import { ngInject } from '@/vue/decorators/angular-interop';
import CurrencyMap from '@/utils/currency.js';
@Component({
    components: {
        VrmPaygoTokenHistoryLine,
        VrmFoldableTable,
    },
})
@AngularLocaleBundle('paygo')
export default class VrmPaygoTokenHistory extends Vue {
    @ngInject()
    ApiService;
    tokens = [];

    @Prop()
    installation;

    site;
    users;

    async mounted() {
        this.installation.then(async siteData => {
            this.site = siteData;
            const paygoTokens = await this.ApiService.call(
                '/installations/' + siteData.id + '/paygo-tokens',
                {},
                'GET',
                false,
                'json',
                false,
            );
            this.tokens = paygoTokens.data.paygoTokens;
            this.tokens.reverse();
            const userData = await this.ApiService.call2(`/installations/${siteData.id}/users`);
            this.users = userData.data.users;
            this.getAllNames();
            this.processTokens();
        });
    }

    updateHistory() {
        this.ApiService.call(
            '/installations/' + this.$route.params.id + '/paygo-tokens',
            {},
            'GET',
            false,
            'json',
            false,
        ).then(response => {
            this.tokens = response.data.paygoTokens;
            this.tokens.reverse();
            this.getAllNames();
            this.processTokens();
        });
    }

    formatTokenCode(code) {
        const codeLength = code.toString().length;

        if (codeLength < 9) {
            for (let i = 0; i < 9 - codeLength; i++) {
                code = '0' + code;
            }
        }
        let formattedCode = code.toString();
        return (
            '' +
            formattedCode.slice(0, 3) +
            ' ' +
            formattedCode.slice(3, 6) +
            ' ' +
            formattedCode.slice(6, 9)
        );
    }

    processTokens() {
        let total = 0;
        for (let i = 0; i < this.tokens.length; i++) {
            this.tokens[i].isPaid = this.tokens[i].isPaid === 1 || this.tokens[i].isPaid === true;
            if (typeof this.tokens[i].amount !== 'number')
                this.tokens[i].amount = Number(this.tokens[i].amount);
            if (this.tokens[i].isPaid) total += this.tokens[i].amount;
        }
        this.$emit('updateTotalAmountSpend', total);
    }

    getAllNames() {
        for (let i = 0; i < this.tokens.length; i++) {
            let name = this.getNameFromUsers(this.tokens[i].idUser);
            if (!name) name = this.tokens[i].idUser;
            this.tokens[i].author = name;
        }
    }

    getNameFromUsers(idUser) {
        for (let i = 0; i < this.users.length; i++) {
            if (this.users[i].idUser === idUser) return this.users[i].name;
        }
        return;
    }

    toggle(itemId) {
        const index = this.expanded.indexOf(itemId);
        if (index >= 0) {
            this.expanded.splice(index, 1);
        } else {
            this.expanded.push(itemId);
        }
    }

    isExpanded(itemId) {
        return this.expanded.includes(itemId);
    }
    getCurrency(currency) {
        return CurrencyMap[currency];
    }

    getTokenTypeText(type) {
        switch (type) {
            case 'setTime':
                return 'Set Time';
            case 'addTime':
                return 'Add Time';
            case 'disable':
                return 'Disable PAYGo';
            default:
                return 'Invalid Token Type';
        }
    }

    isNumber(evt) {
        evt = evt ? evt : window.event;
        var charCode = evt.which ? evt.which : evt.keyCode;
        if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
            evt.preventDefault();
        } else {
            return evt;
        }
    }

    onAmountPaste(evt) {
        const text = evt.clipboardData.getData('text');
        if (!Number.isNaN(Number(text))) {
            return Number(text);
        } else evt.preventDefault();
    }

    onAmountChange(value, id) {
        let data = {
            idPaygoToken: id,
            amount: value,
        };
        this.ApiService.call(
            '/installations/' + this.site.id + '/paygo-tokens',
            data,
            'PUT',
            false,
            'json',
            false,
        ).then(() => {
            this.updateHistory();
        });
    }

    onIsPaidChange(value) {
        let data = value;
        data.isPaid = data.isPaid ? 1 : 0;
        this.ApiService.call(
            '/installations/' + this.site.id + '/paygo-tokens',
            data,
            'PUT',
            false,
            'json',
            false,
        ).then(() => {
            this.processTokens();
        });
    }

    getDate(timeStamp) {
        let date = new Date(timeStamp);
        return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear();
    }
}
</script>
